import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { isNil } from 'lodash';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { ConversationAttributes, SupportedLanguages } from '@pwp-common';

import { RoomUser } from '../../../../common/conversation/chat-component/interfaces';
import { quickExit } from '../../../../common/conversation/quick-exit/quick-exit';

import { IncomingChatWaitingRoomInterface } from './incoming-chat-waiting-room-interface';

@UntilDestroy()
@Component({
  selector: 'app-incoming-chat-waiting-room',
  templateUrl: './incoming-chat-waiting-room.component.html',
  styleUrls: ['./incoming-chat-waiting-room.component.css'],
})
export class IncomingChatWaitingRoomComponent implements OnInit {
  /////////////////////////////////////////////////////////////////////////////////////////////
  // State
  /////////////////////////////////////////////////////////////////////////////////////////////

  public queuePosition: number;

  public redirectURL: string | undefined;

  public isNextInLine: boolean = false;

  public data: IncomingChatWaitingRoomInterface;

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Lifecycle
  /////////////////////////////////////////////////////////////////////////////////////////////

  constructor(
    public ref: DynamicDialogRef,
    dialogConfig: DynamicDialogConfig,
    private translocoService: TranslocoService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    this.data = dialogConfig.data;
  }

  public ngOnInit(): void {
    this.data.client.$room.pipe(untilDestroyed(this)).subscribe((room) => {
      this.handleUpdatedConversationAttributes(room?.attributes);
    });

    this.data.client.$participants.pipe(untilDestroyed(this)).subscribe((participants) => {
      this.handleUpdatedParticipants(participants);
    });
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Buttons
  /////////////////////////////////////////////////////////////////////////////////////////////

  public redirectNow(): void {
    if (this.redirectURL) {
      quickExit(this.redirectURL);
    }
  }

  private handleUpdatedConversationAttributes(conversationAttributes: ConversationAttributes) {
    this.queuePosition = conversationAttributes?.getWaitingRoom()?.getQueuePosition();

    if (isNil(this.queuePosition)) {
      this.ref.close();
      return;
    }

    if (this.queuePosition === 0) {
      this.isNextInLine = true;
      this.redirectURL = conversationAttributes.getRedirectURL();
      this.changeDetectorRef.detectChanges();
      return;
    }

    this.isNextInLine = false;
    for (const lang of SupportedLanguages.all) {
      this.translocoService.setTranslationKey(
        'incoming-chat-waiting-room.body',
        conversationAttributes.getWaitingRoom().getWaitingRoomMessage(lang)!,
        lang.getShortCode(),
      );
    }
    this.redirectURL = conversationAttributes.getRedirectURL();
    this.queuePosition = conversationAttributes.getWaitingRoom()?.getQueuePosition();
    this.changeDetectorRef.detectChanges();
  }

  private handleUpdatedParticipants(participants: RoomUser[]) {
    if (isNil(participants) || participants.length <= 1) {
      return;
    }
    this.ref.close();
  }
}
